import React, { useState } from 'react';

import { formatDateString } from '../util';
import styles from '../CitationImportHistory.module.scss';
import { Inline } from '../../../../components/layout';
import StatBox from './StatBox';
import UndoControl from './UndoControl';
import { SourcesStatBox } from './SourcesStatBox';
import { DuplicatesStatBox } from './DuplicatesStatBox';
import { MergedStatBox } from './MergedStatBox';
import { undoCitationImport } from 'query/review';
import { CitationImport as CitationType } from 'types';
import { createToast, Toast, Icon, IconList, Text } from 'components/core';

// The server sends the workflow-stage name; lowercased here to read mid-sentence.
const visibleCategory = (category: string) => category.toLowerCase();

type Props = {
  reviewImportSources: string[];
  setReviewImportSources: (sourceNames: string[]) => void;
  customReviewImportSources: { id: string; sourceName: string }[];
  setCustomReviewImportSources: any;
  setReviewCanUndo: any;
} & CitationType;

export const CitationImport = ({
  id,
  state,
  filename,
  category,
  createdAt,
  error,
  counts,
  canUndo,
  undoDate,
  importSourcesPlaceholder,
  citationImportSources,
  reviewImportSources,
  setReviewCanUndo,
}: Props) => {
  const [importState, setImportState] = useState(state);

  let loading = false;
  const onUndo = () => {
    if (!loading) {
      const oldState = importState;
      setImportState('undoing');
      setReviewCanUndo(false);
      undoCitationImport(id)
        .then((res) => {
          loading = false;
          if (!res.ok) {
            return Promise.reject();
          }
        })
        .catch(() => {
          setImportState(oldState);
          createToast(
            <Toast type="danger">{'Unable to undo import at this time.'}</Toast>
          );
        });
    }
    loading = true;
  };
  const failedImport = importState === 'failed';
  const hasReferenceErrors = counts.errors > 0;

  return (
    <section data-citation-import-id={id}>
      <Inline spacing={3} className={styles.CitationImport_header}>
        <div className={styles.CitationImport_header_info}>
          <Text component="h3" size="sm">
            {filename || `File import to ${visibleCategory(category)}`}
          </Text>
          <Text variant="weak">{formatDateString(createdAt)}</Text>
        </div>
        <div className={styles.CitationImport_header_actions}>
          <UndoControl
            state={importState}
            canUndo={canUndo}
            undoDate={undoDate}
            onClick={onUndo}
          />
        </div>
      </Inline>
      <ul className={styles.CitationImport_stats}>
        <li>
          <StatBox
            header={`Added to ${visibleCategory(category)}`}
            value={<Text size="xxl">{counts.imported}</Text>}
            solid
          />
        </li>
        <li>
          <StatBox
            header="References"
            value={<Text size="xxl">{counts.citations}</Text>}
          />
        </li>
        <li>
          <DuplicatesStatBox id={id} count={counts.duplicates} />
        </li>
        <li>
          <MergedStatBox count={counts.merged} />
        </li>
        <li>
          <SourcesStatBox
            id={id}
            citations={counts.citations}
            reviewImportSources={reviewImportSources}
            citationImportSources={citationImportSources}
            importSourcesPlaceholder={importSourcesPlaceholder}
          />
        </li>
      </ul>
      {hasReferenceErrors && (
        <Text className={styles.CitationImport_error}>
          <Icon
            icon={IconList.solid.faCircleExclamation}
            className={styles.CitationImport_error_icon}
          />
          {counts.errors} references failed to import due to errors
        </Text>
      )}
      {failedImport && (
        <Text className={styles.CitationImport_error}>
          <Icon
            icon={IconList.solid.faCircleExclamation}
            className={styles.CitationImport_error_icon}
          />
          Importing failed with the error: {error}
        </Text>
      )}
    </section>
  );
};

export default CitationImport;
